/**
 * Custom Wishlist — localStorage-based
 * No external app dependency
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'savvyy_wishlist';

  // ─── Core API ─────────────────────────────────────────────────────────────

  const Wishlist = {
    getItems() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    },

    save(items) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      this.updateBadges();
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: items.length } }));
    },

    add(product) {
      const items = this.getItems();
      if (items.some(item => item.id === product.id)) return false;
      items.push({
        id: product.id,
        handle: product.handle,
        title: product.title,
        price: product.price,
        compareAtPrice: product.compareAtPrice || null,
        image: product.image,
        url: product.url,
        addedAt: Date.now()
      });
      this.save(items);
      return true;
    },

    remove(productId) {
      const items = this.getItems().filter(item => item.id !== productId);
      this.save(items);
      return true;
    },

    toggle(product) {
      if (this.has(product.id)) {
        this.remove(product.id);
        return false;
      } else {
        this.add(product);
        return true;
      }
    },

    has(productId) {
      return this.getItems().some(item => item.id === productId);
    },

    count() {
      return this.getItems().length;
    },

    clear() {
      this.save([]);
    },

    // ─── UI Updates ───────────────────────────────────────────────────────────

    updateBadges() {
      const count = this.count();
      const badges = document.querySelectorAll('[data-wishlist-count]');
      badges.forEach(badge => {
        badge.textContent = count;
        if (count > 0) {
          badge.style.display = 'flex';
        } else {
          badge.style.display = 'none';
        }
      });

      // Also update the header badge
      const headerBadge = document.getElementById('header-wishlist-count');
      if (headerBadge) {
        headerBadge.textContent = count;
        if (count > 0) {
          headerBadge.classList.add('is-visible');
        } else {
          headerBadge.classList.remove('is-visible');
        }
      }
    },

    updateButtons() {
      const buttons = document.querySelectorAll('[data-wishlist-btn]');
      buttons.forEach(btn => {
        const productId = parseInt(btn.dataset.productId, 10);
        if (this.has(productId)) {
          btn.classList.add('is-wishlisted');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('is-wishlisted');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    },

    init() {
      this.updateBadges();
      this.updateButtons();
      this.bindEvents();
    },

    bindEvents() {
      // Delegate click on wishlist buttons
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-wishlist-btn]');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        const product = {
          id: parseInt(btn.dataset.productId, 10),
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          price: btn.dataset.productPrice,
          compareAtPrice: btn.dataset.productComparePrice || null,
          image: btn.dataset.productImage,
          url: btn.dataset.productUrl
        };

        const isAdded = this.toggle(product);
        
        // Animate
        if (isAdded) {
          btn.classList.add('is-wishlisted');
          btn.setAttribute('aria-pressed', 'true');
          // Pulse animation
          btn.classList.add('wishlist-pulse');
          setTimeout(() => btn.classList.remove('wishlist-pulse'), 400);
        } else {
          btn.classList.remove('is-wishlisted');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    }
  };

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Wishlist.init());
  } else {
    Wishlist.init();
  }

  // Expose globally for the wishlist page
  window.SavvyyWishlist = Wishlist;
})();
